import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { isOnFreeStageSelector } from 'features/billing'
import { useWasInNotetakerIntroExperiment } from 'hooks/growth_experiments/use-was-in-notetaker-intro-experiment'
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment'

import { useGetNotetakerFreeMeetingsQuotaQuery } from '../api'
import { WIZARD_MODAL_SEEN_MAX, SESSION_KEY, MR_ONBOARDING_WIZARD } from '../constants'
import { showAcknowledgementModal } from '../modals/wizard_acknowledgement_modal/acknowledgement_modal_slice'
import { getAcknowledgementModalIsUserTriggered } from '../selectors'


export const useShouldDisplayOnboardingWizard = (MiUserDataLoading, MiUserData, update) => {
  const dispatch = useDispatch()
  const ref = useRef()
  const isCurrentSession = sessionStorage.getItem(SESSION_KEY) === 'true'
  const isUserTriggered = useSelector(getAcknowledgementModalIsUserTriggered)
  const wasInNotetakerIntroExperiment = useWasInNotetakerIntroExperiment()
  const isFreeUser = useSelector(isOnFreeStageSelector)
  const { isInExperiment: isInFiveFreeRecapsExperiment } = useNotetakerFiveFreeRecapsExperiment(isFreeUser)
  const { data: userData } = useGetUserQuery()
  const userUuid = userData?.uuid
  const shouldSkipNotetakerQuotaQuery = !isFreeUser || !isInFiveFreeRecapsExperiment || !userUuid
  const {
    data: notetakerQuotaData,
    isLoading: isNotetakerQuotaLoading
  } = useGetNotetakerFreeMeetingsQuotaQuery(
    { userUuid },
    { skip: shouldSkipNotetakerQuotaQuery }
  )
  const hasRemainingFreeMeetings = notetakerQuotaData?.resource?.featureAvailable

  useEffect(() => {
    if (!ref.current && !MiUserDataLoading) {
      const attemptsRemaining = MiUserData?.onboardingWizardModalSeenCount

      if (!shouldSkipNotetakerQuotaQuery && isNotetakerQuotaLoading) {
        return
      }

      if (isUserTriggered || isCurrentSession) {
        ref.current = true
        return
      }

      if (wasInNotetakerIntroExperiment) {
        ref.current = true
        return
      }

      // Prevent modal from showing if admin has disabled notetaker for the organization
      if (MiUserData?.adminDisabledNotetaker) {
        ref.current = true
        return
      }

      if (attemptsRemaining === 0 || MiUserData?.miEnabled) {
        ref.current = true
        return
      }
      // Do not set ref.current when free so that it can be re-evaluated
      // after a potential upgrade by the user
      const isEligibleForFreeTierAutoOpen = isInFiveFreeRecapsExperiment && hasRemainingFreeMeetings
      if (isFreeUser && !isEligibleForFreeTierAutoOpen) {
        return
      }

      // Users first time going through the flow - true
      if (!Number.isInteger(attemptsRemaining)) {
        update({ onboardingWizardModalSeenCount: WIZARD_MODAL_SEEN_MAX })
        ref.current = true
        sessionStorage.setItem(SESSION_KEY, 'true')
        dispatch(showAcknowledgementModal(MR_ONBOARDING_WIZARD.AUTO_LOAD))
        return
      }

      // show modal and decrement
      if (attemptsRemaining > 0) {
        update({ onboardingWizardModalSeenCount: attemptsRemaining - 1 })
        sessionStorage.setItem(SESSION_KEY, 'true')
        ref.current = true
        dispatch(showAcknowledgementModal(MR_ONBOARDING_WIZARD.AUTO_LOAD))
      }
    }
  }, [
    MiUserData,
    MiUserDataLoading,
    ref,
    isCurrentSession,
    update,
    dispatch,
    isUserTriggered,
    wasInNotetakerIntroExperiment,
    isFreeUser,
    isInFiveFreeRecapsExperiment,
    hasRemainingFreeMeetings,
    shouldSkipNotetakerQuotaQuery,
    isNotetakerQuotaLoading
  ])
}
